import * as THREE from 'three';
import { Granit } from 'src/services/granits';
import { getPieceSize } from 'src/utils/configurator.utils';
import { configureHDTexture } from 'src/utils/textureLoader';
import {
  getIndexType,
  getLocalIndex,
  getGlobalIndex,
  isTwoSteleEqual,
  getDimensionsWithGeometry,
} from 'src/utils/3d.utils';

export const checkMonumentSizeWithGranit = (
  pieceGeometry: Float32Array,
  granitMaxLength: string,
  setError: (error: string) => void,
) => {
  const size = {
    x: getPieceSize('x', pieceGeometry),
    y: getPieceSize('y', pieceGeometry),
    z: getPieceSize('z', pieceGeometry),
  };

  if (
    Number.parseFloat(size.x) > Number.parseFloat(granitMaxLength) ||
    Number.parseFloat(size.y) > Number.parseFloat(granitMaxLength) ||
    Number.parseFloat(size.z) > Number.parseFloat(granitMaxLength)
  ) {
    setError(
      `Attention, une de vos pièces dépasse la taille maximale de ce granit (${Number.parseFloat(
        granitMaxLength,
      )}). Elle sera coupée.`,
    );
  }
};

export const changeTexture = (
  granit: Granit,
  indexs: number[],
  buckets: { BUCKET_3D_PIECES?: string },
  activePieces: any[],
  customSteles: any[],
  customPlatings: any[],
  customSoles: any[],
  customBoxes: any[],
  pieces: any[],
  hasAdvancedMode: boolean,
  setActivePieces: (pieces: any[]) => void,
  setCustomSteles: (steles: any[]) => void,
  setCustomPlatings: (platings: any[]) => void,
  setCustomSoles: (soles: any[]) => void,
  setCustomBoxes: (boxes: any[]) => void,
  setError: (error: string | undefined) => void,
) => {
  const loader = new THREE.TextureLoader();
  const loadedTexture = loader.load(
    `${buckets?.BUCKET_3D_PIECES || ''}/${granit.image}?t=${Date.now()}`,
    (texture) => {
      configureHDTexture(texture);
    },
  );

  let newActivePieces = [...activePieces];
  let newCustomStele = [...customSteles];
  let newCustomPlating = [...customPlatings];
  let newCustomSoles = [...customSoles];
  let newCustomBoxes = [...customBoxes];

  for (const index of indexs) {
    let typeOfPiece = 'activePieces';

    typeOfPiece = getIndexType(
      index,
      activePieces,
      customSteles,
      customPlatings,
      customSoles,
      customBoxes,
    );

    if (typeOfPiece === 'activePieces') {
      loadedTexture.wrapS = THREE.RepeatWrapping;
      loadedTexture.wrapT = THREE.MirroredRepeatWrapping;
      loadedTexture.repeat.x = 1;

      newActivePieces = newActivePieces.map((el, elIndex) => {
        if (
          elIndex === index ||
          (activePieces[index].underAssembly &&
            activePieces[index].underAssembly.uuid ===
              activePieces[elIndex].underAssembly?.uuid &&
            !hasAdvancedMode)
        ) {
          if (granit.material !== 'INOX') {
            checkMonumentSizeWithGranit(
              pieces[index].geometry.attributes.position.array,
              String(granit.maxLength),
              setError,
            );
          }
          return {
            ...el,
            granit,
            texture: loadedTexture,
          };
        }
        return el;
      });
    } else if (typeOfPiece === 'customStele') {
      loadedTexture.wrapS = THREE.RepeatWrapping;
      loadedTexture.wrapT = THREE.MirroredRepeatWrapping;
      loadedTexture.offset.x = 0.5;
      loadedTexture.repeat.x = 0.5;
      loadedTexture.repeat.y = 0.5;
      newCustomStele = newCustomStele.map((el, elIndex) => {
        if (index === activePieces.length + elIndex) {
          const extrudeGeometry = new THREE.ExtrudeGeometry(
            el.shape,
            el.extrudeSettings,
          );
          if (granit.material !== 'INOX') {
            checkMonumentSizeWithGranit(
              // @ts-ignore
              extrudeGeometry.attributes.position.array,
              String(granit.maxLength),
              setError,
            );
          }
          return {
            ...el,
            granit,
            texture: loadedTexture,
          };
        }
        return el;
      });
    } else if (typeOfPiece === 'customPlating') {
      loadedTexture.wrapS = THREE.RepeatWrapping;
      loadedTexture.wrapT = THREE.MirroredRepeatWrapping;
      loadedTexture.offset.x = 0.5;
      loadedTexture.repeat.x = 0.5;
      loadedTexture.repeat.y = 0.5;
      newCustomPlating = newCustomPlating.map((el, elIndex) => {
        const deltaIndex =
          (activePieces.length ?? 0) + (customSteles.length ?? 0);
        if (
          index >= deltaIndex &&
          index < deltaIndex + (customPlatings.length ?? 0)
        ) {
          if (hasAdvancedMode && index - deltaIndex !== elIndex) return el;
          const extrudeGeometry = new THREE.ExtrudeGeometry(el.shape, {
            curveSegments: 12,
            steps: 2,
            depth: el.shapeDetails.depth,
            bevelEnabled: false,
          });
          if (granit.material !== 'INOX') {
            checkMonumentSizeWithGranit(
              // @ts-ignore
              extrudeGeometry.attributes.position.array,
              String(granit.maxLength),
              setError,
            );
          }

          return {
            ...el,
            granit,
            texture: loadedTexture,
          };
        }
        return el;
      });
    } else if (typeOfPiece === 'customSole') {
      loadedTexture.wrapS = THREE.RepeatWrapping;
      loadedTexture.wrapT = THREE.MirroredRepeatWrapping;
      loadedTexture.offset.x = 0.5;
      loadedTexture.repeat.x = 0.5;
      loadedTexture.repeat.y = 0.5;
      newCustomSoles = newCustomSoles.map((el, elIndex) => {
        const deltaIndex =
          (activePieces.length ?? 0) +
          (customSteles.length ?? 0) +
          (customPlatings.length ?? 0);
        if (
          index >= deltaIndex &&
          index < deltaIndex + (customSoles.length ?? 0)
        ) {
          if (hasAdvancedMode && index - deltaIndex !== elIndex) return el;
          const extrudeGeometry = new THREE.ExtrudeGeometry(el.shape, {
            curveSegments: 12,
            steps: 2,
            depth: el.shapeDetails.depth,
            bevelEnabled: false,
          });
          if (granit.material !== 'INOX') {
            checkMonumentSizeWithGranit(
              // @ts-ignore
              extrudeGeometry.attributes.position.array,
              String(granit.maxLength),
              setError,
            );
          }

          return {
            ...el,
            granit,
            texture: loadedTexture,
          };
        }
        return el;
      });
    } else if (typeOfPiece === 'customBox') {
      loadedTexture.wrapS = THREE.RepeatWrapping;
      loadedTexture.wrapT = THREE.MirroredRepeatWrapping;
      loadedTexture.offset.x = 0.5;
      loadedTexture.repeat.x = 0.5;
      loadedTexture.repeat.y = 0.5;
      newCustomBoxes = newCustomBoxes.map((el, elIndex) => {
        const deltaIndex =
          (activePieces.length ?? 0) +
          (customSteles.length ?? 0) +
          (customPlatings.length ?? 0) +
          (customSoles.length ?? 0);
        if (
          index >= deltaIndex &&
          index < deltaIndex + (customBoxes.length ?? 0)
        ) {
          if (hasAdvancedMode && index - deltaIndex !== elIndex) return el;
          const extrudeGeometry = new THREE.ExtrudeGeometry(el.shape, {
            curveSegments: 12,
            steps: 2,
            depth: el.shapeDetails.depth,
            bevelEnabled: false,
          });
          if (granit.material !== 'INOX') {
            checkMonumentSizeWithGranit(
              // @ts-ignore
              extrudeGeometry.attributes.position.array,
              String(granit.maxLength),
              setError,
            );
          }

          return {
            ...el,
            granit,
            texture: loadedTexture,
          };
        }
        return el;
      });
    }
  }

  setActivePieces(newActivePieces);
  setCustomPlatings(newCustomPlating);
  setCustomSteles(newCustomStele);
  setCustomSoles(newCustomSoles);
  setCustomBoxes(newCustomBoxes);
};

export const selectedTexture = (
  pieceIndex: number,
  activePieces: any[],
  customSteles: any[],
  customPlatings: any[],
  customSoles: any[],
  customBoxes: any[],
) => {
  const typeOfPiece = getIndexType(
    pieceIndex,
    activePieces,
    customSteles,
    customPlatings,
    customSoles,
    customBoxes,
  );
  const localIndex = getLocalIndex(
    pieceIndex,
    activePieces,
    customSteles,
    customPlatings,
    customSoles,
    customBoxes,
  );

  if (typeOfPiece === 'activePieces') {
    return activePieces[localIndex]?.granit;
  }
  if (typeOfPiece === 'customStele') {
    return customSteles[localIndex]?.granit;
  }
  if (typeOfPiece === 'customPlating') {
    return customPlatings[localIndex]?.granit;
  }
  if (typeOfPiece === 'customSole') {
    return customSoles[localIndex]?.granit;
  }
  if (typeOfPiece === 'customBox') {
    return customBoxes[localIndex]?.granit;
  }
};

export const selectPieceWithGranitId = (
  granitId: string,
  activePieces: any[],
  customSteles: any[],
  customPlatings: any[],
  customSoles: any[],
  customBoxes: any[],
  setPieceIndex: (index: number) => void,
) => {
  for (const [elIndex, el] of activePieces.entries()) {
    if (el.granit?.id === granitId) {
      const globalIndex = getGlobalIndex(
        elIndex,
        'activePieces',
        activePieces,
        customSteles,
        customPlatings,
        customSoles,
        customBoxes,
      );
      setPieceIndex(globalIndex);
      return;
    }
  }
  for (const [elIndex, el] of customSteles.entries()) {
    if (el.granit?.id === granitId) {
      const globalIndex = getGlobalIndex(
        elIndex,
        'customStele',
        activePieces,
        customSteles,
        customPlatings,
        customSoles,
        customBoxes,
      );
      setPieceIndex(globalIndex + elIndex);
      return;
    }
  }
  for (const [elIndex, el] of customPlatings.entries()) {
    if (el.granit?.id === granitId) {
      const globalIndex = getGlobalIndex(
        elIndex,
        'customPlating',
        activePieces,
        customSteles,
        customPlatings,
        customSoles,
        customBoxes,
      );
      setPieceIndex(globalIndex + elIndex);
      return;
    }
  }
  for (const [elIndex, el] of customSoles.entries()) {
    if (el.granit?.id === granitId) {
      const globalIndex = getGlobalIndex(
        elIndex,
        'customSole',
        activePieces,
        customSteles,
        customPlatings,
        customSoles,
        customBoxes,
      );
      setPieceIndex(globalIndex + elIndex);
      return;
    }
  }
  for (const [elIndex, el] of customBoxes.entries()) {
    if (el.granit?.id === granitId) {
      const globalIndex = getGlobalIndex(
        elIndex,
        'customBox',
        activePieces,
        customSteles,
        customPlatings,
        customSoles,
        customBoxes,
      );
      setPieceIndex(globalIndex + elIndex);
      return;
    }
  }
};

export type StockToApply = {
  stockPieces?: Array<{
    pieceId: number;
    granitId?: string;
    granit?: { id: string; name: string; fabrication: string };
  }>;
  stockSteles?: Array<{
    granitId: string;
    granit?: { id: string; name: string; fabrication: string };
    baseIndex?: number;
    boxDownWidth?: number;
    boxHeight?: number;
    boxLeftHeight?: number;
    boxRightHeight?: number;
    boxUpWidth?: number;
    depth?: number;
    headIndex?: number;
  }>;
  stockPlatings?: Array<{
    granitId: string;
    granit?: { id: string; name: string; fabrication: string };
  }>;
  stockSoles?: Array<{
    granitId: string;
    granit?: { id: string; name: string; fabrication: string };
  }>;
  stockBoxes?: Array<{
    granitId: string;
    granit?: { id: string; name: string; fabrication: string };
  }>;
};

/**
 * Applique les granits d'un stock à toutes les pièces correspondantes du monument.
 * Regroupe les pièces par granit pour optimiser les appels à changeTexture.
 */
export const applyStockGranits = (
  stock: StockToApply,
  granits: any[],
  buckets: { BUCKET_3D_PIECES?: string },
  activePieces: any[],
  customSteles: any[],
  customPlatings: any[],
  customSoles: any[],
  customBoxes: any[],
  pieces: any[],
  hasAdvancedMode: boolean,
  setActivePieces: (pieces: any[]) => void,
  setCustomSteles: (steles: any[]) => void,
  setCustomPlatings: (platings: any[]) => void,
  setCustomSoles: (soles: any[]) => void,
  setCustomBoxes: (boxes: any[]) => void,
  setError: (error: string | undefined) => void,
) => {
  // Map pour regrouper les indices par granitId
  const granitToIndices: Map<string, number[]> = new Map();

  // Traiter les stockPieces - matcher par pieceId
  if (stock.stockPieces) {
    for (const stockPiece of stock.stockPieces) {
      // Trouver l'index dans activePieces qui correspond à ce pieceId
      const activePieceIndex = activePieces.findIndex(
        (ap) => ap.id === stockPiece.pieceId,
      );

      if (activePieceIndex !== -1) {
        const granitId = stockPiece.granitId;
        if (!granitId) continue;
        if (!granitToIndices.has(granitId)) {
          granitToIndices.set(granitId, []);
        }
        granitToIndices.get(granitId)!.push(activePieceIndex);
      }
    }
  }

  // Traiter les stockSteles - matcher par paramètres de stèle
  if (stock.stockSteles) {
    for (const stockStele of stock.stockSteles) {
      // Trouver la stèle correspondante dans customSteles
      const steleIndex = customSteles.findIndex((cs) => {
        return (
          cs.baseIndex === stockStele.baseIndex &&
          cs.headIndex === stockStele.headIndex &&
          Math.abs(Number(cs.boxHeight) - Number(stockStele.boxHeight)) < 1 &&
          Math.abs(Number(cs.boxUpWidth) - Number(stockStele.boxUpWidth)) < 1 &&
          Math.abs(Number(cs.depth) - Number(stockStele.depth)) < 1
        );
      });

      if (steleIndex !== -1) {
        const globalIndex = getGlobalIndex(
          steleIndex,
          'customStele',
          activePieces,
          customSteles,
          customPlatings,
          customSoles,
        );
        const granitId = stockStele.granitId;
        if (!granitToIndices.has(granitId)) {
          granitToIndices.set(granitId, []);
        }
        granitToIndices.get(granitId)!.push(globalIndex);
      }
    }
  }

  // Traiter les stockPlatings - matcher par index (ordre)
  if (stock.stockPlatings) {
    stock.stockPlatings.forEach((stockPlating, platingIndex) => {
      if (platingIndex < customPlatings.length) {
        const globalIndex = getGlobalIndex(
          platingIndex,
          'customPlating',
          activePieces,
          customSteles,
          customPlatings,
          customSoles,
        );
        const granitId = stockPlating.granitId;
        if (!granitToIndices.has(granitId)) {
          granitToIndices.set(granitId, []);
        }
        granitToIndices.get(granitId)!.push(globalIndex);
      }
    });
  }

  // Traiter les stockBoxes - matcher par index (ordre)
  if (stock.stockBoxes) {
    stock.stockBoxes.forEach((stockBox, boxIndex) => {
      if (boxIndex < customBoxes.length) {
        const globalIndex = getGlobalIndex(
          boxIndex,
          'customBox',
          activePieces,
          customSteles,
          customPlatings,
          customSoles,
          customBoxes,
        );
        const granitId = stockBox.granitId;
        if (!granitToIndices.has(granitId)) {
          granitToIndices.set(granitId, []);
        }
        granitToIndices.get(granitId)!.push(globalIndex);
      }
    });
  }

  // Traiter les stockSoles - matcher par index (ordre)
  if (stock.stockSoles) {
    stock.stockSoles.forEach((stockSole, soleIndex) => {
      if (soleIndex < customSoles.length) {
        const globalIndex = getGlobalIndex(
          soleIndex,
          'customSole',
          activePieces,
          customSteles,
          customPlatings,
          customSoles,
        );
        const granitId = stockSole.granitId;
        if (!granitToIndices.has(granitId)) {
          granitToIndices.set(granitId, []);
        }
        granitToIndices.get(granitId)!.push(globalIndex);
      }
    });
  }

  // Maintenant appliquer changeTexture pour chaque groupe de granit
  // On doit le faire de manière séquentielle pour éviter les conflits d'état
  let currentActivePieces = [...activePieces];
  let currentCustomSteles = [...customSteles];
  let currentCustomPlatings = [...customPlatings];
  let currentCustomSoles = [...customSoles];
  let currentCustomBoxes = [...customBoxes];
  for (const [granitId, indices] of granitToIndices.entries()) {
    const granit = granits.find((g) => g.id === granitId);
    if (!granit || indices.length === 0) continue;

    // Créer des setters qui mettent à jour les variables locales
    const tempSetActivePieces = (newPieces: any[]) => {
      currentActivePieces = newPieces;
    };
    const tempSetCustomSteles = (newSteles: any[]) => {
      currentCustomSteles = newSteles;
    };
    const tempSetCustomPlatings = (newPlatings: any[]) => {
      currentCustomPlatings = newPlatings;
    };
    const tempSetCustomSoles = (newSoles: any[]) => {
      currentCustomSoles = newSoles;
    };
    const tempSetCustomBoxes = (newBoxes: any[]) => {
      currentCustomBoxes = newBoxes;
    };
    changeTexture(
      granit,
      indices,
      buckets,
      currentActivePieces,
      currentCustomSteles,
      currentCustomPlatings,
      currentCustomSoles,
      currentCustomBoxes,
      pieces,
      hasAdvancedMode,
      tempSetActivePieces,
      tempSetCustomSteles,
      tempSetCustomPlatings,
      tempSetCustomSoles,
      tempSetCustomBoxes,
      setError,
    );
  }

  // Appliquer tous les changements en une fois à la fin
  setActivePieces(currentActivePieces);
  setCustomSteles(currentCustomSteles);
  setCustomPlatings(currentCustomPlatings);
  setCustomSoles(currentCustomSoles);
};
